import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";
import { fetchVideo } from "../../api/newsService";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import MotionReveal from "../motion/MotionReveal";

const VideoLibrarySection = () => {
  const [postVideo, setPostVideo] = useState([]);
  const [desktopCardsVisible, setDesktopCardsVisible] = useState(false);
  const [mobileCardsVisible, setMobileCardsVisible] = useState(false);
  const swiperRef = useRef(null);
  const { ref: desktopSectionRef, inView: desktopSectionInView } = useInView({
    triggerOnce: true,
    threshold: 0.12,
    rootMargin: "0px 0px -14% 0px",
  });
  const { ref: mobileSectionRef, inView: mobileSectionInView } = useInView({
    triggerOnce: true,
    threshold: 0.12,
    rootMargin: "0px 0px -14% 0px",
  });

  useEffect(() => {
    const getVideos = async () => {
      try {
        const res = await fetchVideo();
        if (res?.data?.data?.lstVideo?.length > 0) {
          setPostVideo(res.data.data.lstVideo);
        }
      } catch (error) {
        setPostVideo([]);
      }
    };

    getVideos();
  }, []);

  useEffect(() => {
    if (!desktopSectionInView) return;

    const timer = window.setTimeout(() => {
      setDesktopCardsVisible(true);
    }, 180);

    return () => window.clearTimeout(timer);
  }, [desktopSectionInView]);

  useEffect(() => {
    if (!mobileSectionInView) return;

    const timer = window.setTimeout(() => {
      setMobileCardsVisible(true);
    }, 180);

    return () => window.clearTimeout(timer);
  }, [mobileSectionInView]);

  const visibleVideos = postVideo.slice(0, 6);

  if (visibleVideos.length === 0) {
    return null;
  }

  return (
    <section className="w-full bg-white py-10 md:py-14">
      <div className="mx-auto max-w-7xl px-4 2xl:max-w-[1440px]">
        <MotionReveal direction="up" delay={0.2}>
          <div className="text-center">
            <div className="relative inline-flex flex-col items-center">
              <div className="relative flex items-center">
                <h2 className="section-tech-title text-[24px] md:text-[32px] lg:text-[44px] font-bold text-[#084FA8]">
                  Thư viện video
                </h2>
              </div>
            </div>
            <p className="mx-auto mt-4 max-w-[760px] text-base font-medium leading-8 text-slate-600 md:text-[19px]">
              Khám phá các video nổi bật, hoạt động, sự kiện và nội dung truyền thông tiêu biểu.
            </p>
          </div>
        </MotionReveal>

        <div ref={desktopSectionRef} className="mt-8 hidden grid-cols-2 gap-5 md:grid lg:grid-cols-3">
          {visibleVideos.map((video, index) => (
            <div
              key={video.id || index}
              className={`transform-gpu transition-all duration-700 ease-out ${
                desktopCardsVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
              }`}
              style={{ transitionDelay: `${180 + index * 90}ms` }}
            >
              <Link
                to={`/thu-vien-video/${video.id}`}
                className="group block overflow-hidden rounded-[20px] bg-white shadow-[0_8px_28px_rgba(15,23,42,0.08)] transition-all duration-300 hover:shadow-[0_18px_40px_rgba(15,23,42,0.16)]"
              >
                <div className="relative aspect-video overflow-hidden bg-slate-100">
                  <img
                    src={video.image}
                    alt={video.title}
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-black/10 transition-colors duration-300 group-hover:bg-black/20" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full border border-white/60 bg-white/20 backdrop-blur-sm transition-all duration-300 group-hover:scale-110 group-hover:bg-white/30">
                      <Play className="ml-1 h-6 w-6 fill-white text-white" />
                    </span>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>

        <div ref={mobileSectionRef} className="mt-8 md:hidden">
          <Swiper
            modules={[Pagination]}
            className="video-library-swiper"
            centeredSlides={visibleVideos.length > 1}
            spaceBetween={14}
            slidesPerView={1.4}
            slidesPerGroup={1}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            breakpoints={{
              480: { slidesPerView: 1.55, slidesPerGroup: 1, spaceBetween: 14 },
              640: { slidesPerView: 2, slidesPerGroup: 2, spaceBetween: 16 },
            }}
            pagination={{ clickable: true }}
          >
            {visibleVideos.map((video, index) => (
              <SwiperSlide key={video.id || index} className="!h-auto">
                <div
                  className={`h-full transform-gpu transition-all duration-700 ease-out ${
                    mobileCardsVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
                  }`}
                  style={{ transitionDelay: `${180 + index * 90}ms` }}
                >
                  <Link
                    to={`/thu-vien-video/${video.id}`}
                    className="group block h-full overflow-hidden rounded-[20px] bg-white shadow-[0_6px_24px_rgba(15,23,42,0.08)] transition-all duration-300 hover:shadow-[0_18px_36px_rgba(15,23,42,0.16)]"
                  >
                    <div className="relative aspect-video overflow-hidden bg-slate-100">
                      <img
                        src={video.image}
                        alt={video.title}
                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                      />
                      <div className="absolute inset-0 bg-black/10" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/60 bg-white/20 backdrop-blur-sm">
                          <Play className="ml-1 h-5 w-5 fill-white text-white" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          <div className="mt-5 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => swiperRef.current?.slidePrev()}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 shadow-md transition-all duration-150 active:scale-90 active:bg-gray-100"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            <button
              type="button"
              onClick={() => swiperRef.current?.slideNext()}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 shadow-md transition-all duration-150 active:scale-90 active:bg-gray-100"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VideoLibrarySection;
